import { Component, input, output, signal, computed, ChangeDetectionStrategy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { FormMode } from 'app/core/constants/form-mode';
import { flattenMenusAsTree, menuParentId } from '../../utils/menu-tree.util';
import { AppSettings } from '@services/settings/app-settings.service';
import { ListCardComponent } from 'app/core/components/list-card/list-card.component';
import { MenuActionOption } from 'app/core/constants/role-actions.constant';

@Component({
    selector: 'app-role-menus-list',
    standalone: true,
    imports: [CommonModule, FormsModule, NgSelectModule, ListCardComponent],
    templateUrl: './role-menus-list.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./role-menus-list.component.css']
})
export class RoleMenusListComponent {
    menus = input.required<any[]>();
    /** @deprecated Ya no condiciona agregar/eliminar; se usa canManage. */
    mode = input<FormMode>(FormMode.VIEW);
    /** Independiente del Editar del formulario de rol (como entidades en usuarios). */
    canManage = input<boolean>(true);
    /** Checks y alta/baja de menús solo con el draft local. */
    editing = input<boolean>(false);
    formMode = FormMode;

    onRemove = output<any>();
    onAdd = output<void>();
    onPermissionChange = output<any>();
    onActionsChange = output<{ menuId: any, actions: string[] }>();
    onEdit = output<void>();
    onSave = output<void>();
    onCancel = output<void>();

    public appSettings = inject(AppSettings);
    searchTerm = signal<string>('');

    filteredMenus = computed(() => {
        const ordered = flattenMenusAsTree(this.menus() || []);
        const term = this.searchTerm().toLowerCase();
        if (!term) return ordered;
        return ordered.filter((m) => String(m.name || '').toLowerCase().includes(term));
    });

    isChild(menu: any): boolean {
        return menuParentId(menu) != null;
    }

    removeMenu(id: any) {
        this.onRemove.emit(id);
    }

    addMenu() {
        this.onAdd.emit();
    }

    togglePermission(menuId: any, permission: string) {
        if (!this.canManage() || !this.editing()) return;
        this.onPermissionChange.emit({ menuId, permission });
    }

    /** Lee flag CRUD soportando RoleMenu / role_menu y camel/snake. */
    perm(menu: any, key: string): boolean {
        const rm = menu?.RoleMenu || menu?.role_menu || {};
        const snake = key.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
        return !!(rm[key] ?? rm[snake]);
    }

    /** Obtiene el catalogo de acciones disponibles de la BD. Retorna null si no tiene. */
    availableActions(menu: any): MenuActionOption[] | null {
        if (!menu) return null;
        const acts = menu.availableActions ?? menu.available_actions;
        return Array.isArray(acts) ? (acts.length > 0 ? acts : null) : this._parseActionsJson(menu, acts);
    }

    private _parseActionsJson(menu: any, raw: unknown): MenuActionOption[] | null {
        if (typeof raw !== 'string' || !raw.trim()) return null;
        try {
            const parsed = JSON.parse(raw);
            menu.availableActions = parsed;
            return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
        } catch {
            menu.availableActions = null;
            return null;
        }
    }

    /** Resuelve las acciones asignadas a este menu con referencia estable. */
    menuActions(menu: any): string[] {
        const rm = menu?.RoleMenu ?? menu?.role_menu;
        if (!rm) return [];
        if (!Array.isArray(rm.actions)) rm.actions = this._safeParseArray(rm.actions);
        return rm.actions;
    }

    private _safeParseArray(val: unknown): string[] {
        if (typeof val !== 'string' || !val.trim()) return [];
        try {
            const res = JSON.parse(val);
            return Array.isArray(res) ? res : [];
        } catch {
            return [];
        }
    }

    /** Emite el cambio de acciones operativas seleccionadas. */
    onSelectActions(menu: any, selected: string[]): void {
        if (!this.canManage() || !this.editing() || !menu) return;
        const next = selected ?? [];
        const rm = menu.RoleMenu ?? menu.role_menu ?? (menu.RoleMenu = {});
        rm.actions = next;
        this.onActionsChange.emit({ menuId: menu.id, actions: next });
    }
}
